(() => {
  if (customElements.get('weldingshop-variant-picker')) return;

  class WeldingshopVariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-ws-variants]')?.textContent || '[]');
      this.groups = [...this.querySelectorAll('[data-ws-option-position]')];
      this.selected = this.groups.map((group) =>
        group.querySelector('[data-ws-option-value].is-selected')?.dataset.wsOptionValue || ''
      );
      this.sortValuesByProductOrder();
      this.abortController = null;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ws-option-value]');
        if (!button || button.disabled) return;
        this.select(Number(button.closest('[data-ws-option-position]').dataset.wsOptionPosition) - 1,
          button.dataset.wsOptionValue);
      });
      if (!this.variantForExactSelection()) this.reconcileSelection();
      const correctedVariant = this.reconcileHiddenSelection();
      this.render();
      if (correctedVariant) this.applyVariant(correctedVariant);
    }

    sortValuesByProductOrder() {
      this.groups.forEach((group, position) => {
        const values = [...new Set(
          this.variants.map((variant) => variant.options[position])
        )];
        const buttons = new Map(
          [...group.querySelectorAll('[data-ws-option-value]')].map((button) => [
            button.dataset.wsOptionValue,
            button
          ])
        );
        const container = group.querySelector('.ws-variant-picker__values');
        values.forEach((value) => {
          const button = buttons.get(value);
          if (button) container?.appendChild(button);
        });
      });
    }

    variantForExactSelection() {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === this.selected[index])
      );
    }

    candidatesFor(position, value) {
      return this.variants.filter((variant) => {
        if (variant.options[position] !== value) return false;
        return this.selected.slice(0, position).every((selected, index) =>
          !selected || variant.options[index] === selected
        );
      });
    }

    reconcileSelection(preferredPosition = -1, preferredValue = '') {
      let candidates = this.variants;
      if (preferredPosition >= 0) {
        const preferred = this.candidatesFor(preferredPosition, preferredValue);
        candidates = preferred.length ? preferred : this.variants.filter(
          (variant) => variant.options[preferredPosition] === preferredValue
        );
      }
      let variant = candidates.find((item) => item.available) || candidates[0];
      if (!variant) variant = this.variants.find((item) => item.available) || this.variants[0];
      if (variant) this.selected = [...variant.options];
      return variant;
    }

    select(position, value) {
      this.selected[position] = value;
      const exact = this.variantForExactSelection();
      let variant = exact || this.reconcileSelection(position, value);
      variant = this.reconcileHiddenSelection() || variant;
      if (!variant) return;
      this.selected = [...variant.options];
      this.render();
      this.applyVariant(variant);
    }

    valueExistsForPrefix(position, value) {
      return this.variants.some((variant) => {
        if (variant.options[position] !== value) return false;
        return this.selected.slice(0, position).every((selected, index) =>
          variant.options[index] === selected
        );
      });
    }

    valueIsOrderableForPrefix(position, value) {
      return this.variants.some((variant) => {
        if (!variant.available || variant.options[position] !== value) return false;
        return this.selected.slice(0, position).every((selected, index) =>
          variant.options[index] === selected
        );
      });
    }

    shouldHideValue(group, position, value) {
      const optionName = (group.dataset.wsOptionName || '').trim().toLowerCase();
      if (optionName !== 'koopvorm' || value !== 'Stuk') return false;
      return !this.valueIsOrderableForPrefix(position, value);
    }

    reconcileHiddenSelection() {
      const hiddenIndex = this.groups.findIndex((group, position) =>
        this.shouldHideValue(group, position, this.selected[position])
      );
      if (hiddenIndex < 0) return null;

      const replacement = this.variants.find((variant) => {
        if (!variant.available) return false;
        return this.selected.slice(0, hiddenIndex).every((selected, index) =>
          !selected || variant.options[index] === selected
        );
      }) || this.variants.find((variant) => variant.available);

      if (!replacement) return null;
      this.selected = [...replacement.options];
      return replacement;
    }

    render() {
      this.groups.forEach((group, position) => {
        group.querySelectorAll('[data-ws-option-value]').forEach((button) => {
          const selected = button.dataset.wsOptionValue === this.selected[position];
          const exists = this.valueExistsForPrefix(position, button.dataset.wsOptionValue);
          const orderable = this.valueIsOrderableForPrefix(position, button.dataset.wsOptionValue);
          const hidden = this.shouldHideValue(group, position, button.dataset.wsOptionValue);
          button.hidden = hidden;
          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', !exists);
          button.classList.toggle('is-sold-out', exists && !orderable);
          button.disabled = !exists;
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          button.setAttribute('aria-disabled', !exists ? 'true' : 'false');
        });
      });
    }

    applyVariant(variant) {
      const formId = this.dataset.productFormId;
      document.querySelectorAll(`#${CSS.escape(formId)}, #product-form-installment`).forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
      this.setText(`.ProductSku-${CSS.escape(this.dataset.sectionId)}`, variant.sku);
      this.setText(`.ProductBarcode-${CSS.escape(this.dataset.sectionId)}`, variant.barcode);
      const gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
      if (variant.featured_media_id && gallery?.setActiveMedia) {
        const mediaId = String(variant.featured_media_id);
        if (gallery.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`)) {
          gallery.setActiveMedia(mediaId, gallery.getAttribute('hide-variants') === 'true');
        }
      }
      this.refreshVariantContent(variant);
    }

    setText(selector, value) {
      document.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || '';
        element.closest('.product-info-meta-item')?.classList.toggle('visually-hidden', !value);
      });
    }

    async refreshVariantContent(variant) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setAttribute('aria-busy', 'true');
      try {
        const url = `${this.dataset.productUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}&variant=${variant.id}`;
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Variantweergave laden mislukt (${response.status})`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const selector = `.product-block-content-${CSS.escape(this.dataset.sectionId)}.variant-changed-update`;
        const currentBlocks = [...document.querySelectorAll(selector)];
        const freshBlocks = [...html.querySelectorAll(selector)];
        currentBlocks.forEach((block, index) => {
          if (freshBlocks[index]) block.replaceWith(freshBlocks[index]);
        });
        const currentForm = document.getElementById(`product-form-${this.dataset.sectionId}`)?.closest('product-form');
        const freshForm = html.getElementById(`product-form-${this.dataset.sectionId}`)?.closest('product-form');
        if (currentForm && freshForm) currentForm.replaceWith(freshForm);
        const detail = { variant };
        document.dispatchEvent(new CustomEvent('product:variant-change', { detail }));
        if (window.publish && window.PUB_SUB_EVENTS?.productVariantChange) {
          window.publish(window.PUB_SUB_EVENTS.productVariantChange, {
            sectionId: this.dataset.sectionId, html, variant
          });
        }
        const status = this.querySelector('[data-ws-status]');
        if (status) status.textContent = '';
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          const status = this.querySelector('[data-ws-status]');
          if (status) status.textContent = 'De productinformatie kon niet direct worden vernieuwd. Probeer de keuze opnieuw.';
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('weldingshop-variant-picker', WeldingshopVariantPicker);
})();
